import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  InputField,
  Divider,
  StepNextButton,
  Modal,
  Accordion,
  Button,
} from "../../../components";
import {
  ArrowLeftIcon,
  BankIcon,
  DashboardLayoutIcon,
  InfoIcon,
  MonthlyStatementIcon,
  PosReceiptsIcon,
  HyphenIcon,
  PlusIcon,
} from "../../../assets/icons";
import { useStepper } from "../Stepper";
import { useSaveMastercardMerchantIds } from "../../../hooks";
import { useLoanApplication } from "../../../contexts";
import { LoanApplicationStage } from "../../../hooks/api/types";

const infoItems = [
  {
    icon: <DashboardLayoutIcon className="cx-mr-2" />,
    title: "Check Your Payment Processor Dashboard",
    content:
      "Log into your payment processor’s account (e.g., Stripe, PayPal, Square) and navigate to the “Account” or “Settings” section. Look for “Merchant ID” or “MID,” which is typically listed under your business details or transaction settings.",
  },
  {
    icon: <PosReceiptsIcon className="cx-mr-2" />,
    title: "Check POS Terminals or Receipts",
    content: "Check POS Terminals or Receipts content comes here",
  },
  {
    icon: <BankIcon className="cx-mr-2" />,
    title: "Contact Your Payment Processor",
    content: "Contact Your Payment Processor content comes here",
  },
  {
    icon: <MonthlyStatementIcon className="cx-mr-2" />,
    title: "On Your Monthly Statement",
    content: "On Your Monthly Statement content comes here",
  },
];

const validationSchema = Yup.object({
  merchantIds: Yup.array()
    .of(Yup.string().max(15, "Merchant ID must be at most 15 characters"))
    .max(5, "You can only add up to 5 Merchant IDs"),
});

type Props = {};

export const MerchantIDInputForm: React.FC<Props> = () => {
  const [openInfo, setOpenInfo] = useState(false);
  const [duplicatedMerchant, setDuplicatedMerchant] = useState(false);
  const { nextStep } = useStepper();
  const { mutateAsync: saveMastercardMerchantIds, isLoading } =
    useSaveMastercardMerchantIds();
  const {
    state: { id },
    actions: { update },
  } = useLoanApplication();

  // next step is always documents upload
  const moveNext = async () => {
    await update({
      stage: LoanApplicationStage.DOCUMENTS_UPLOAD,
    });

    nextStep();
  };

  const {
    handleSubmit,
    values,
    setFieldValue,
    touched,
    errors,
    setFieldTouched,
    isSubmitting,
    setSubmitting,
  } = useFormik({
    initialValues: {
      merchantIds: [""], // Initial array of one empty merchant ID
    },
    validationSchema,
    onSubmit: (values) => {
      setSubmitting(true);
      if (values.merchantIds.length === 0) {
        moveNext();
        return;
      }
      values.merchantIds = getUniqueIds(values.merchantIds);
      saveMastercardMerchantIds({
        merchantIds: values.merchantIds.filter((id) => id !== ""), // Remove empty strings,
        loanApplicationId: id,
      })
        .then(() => {
          moveNext();
        })
        .catch((error) => {
          console.error("Error saving Merchant IDs:", error);
          setSubmitting(false);
        });
    },
  });
  // Run only when values.merchantIds changes
  useEffect(() => {
    const mIds = values.merchantIds || [];
    if (mIds.length) {
      const hasDuplicated = new Set(mIds).size !== mIds.length;
      setDuplicatedMerchant(hasDuplicated);
    } else {
      setDuplicatedMerchant(false);
    }
  }, [values?.merchantIds]);

  const getUniqueIds = (mIds: string[] = []): string[] => {
    return Array.from(new Set(mIds));
  };

  const addMerchantId = () => {
    if (values.merchantIds.length < 5) {
      setFieldValue("merchantIds", [...values.merchantIds, ""]);
    }
  };

  const removeMerchantId = (index: number) => {
    const updatedMerchantIds = values.merchantIds.filter((_, i) => i !== index);
    setFieldValue("merchantIds", updatedMerchantIds);
  };

  const updateMerchantId = (value: string, index: number) => {
    const updatedMerchantIds = [...values.merchantIds];
    updatedMerchantIds[index] = value;
    setFieldValue("merchantIds", updatedMerchantIds);
  };

  return (
    <div className="cx-flex cx-flex-col cx-items-center cx-w-full cx-px-4 sm:cx-px-0">
      <div className="cx-text-text-primary cx-font-bold cx-text-2xl sm:cx-text-3xl cx-text-center cx-mb-10">
        {"Add your Merchant ID"}
        <p className="cx-text-text-secondary cx-font-light cx-text-sm">
          {
            "Please provide your Merchant ID to help us verify your financial information."
          }
        </p>
        <p className="cx-text-text-secondary cx-font-light cx-text-sm">
          {
            "Adding MID can give you access to faster loan approval and higher credit limits."
          }
        </p>
      </div>

      <div className="cx-w-full cx-max-w-[400px] sm:cx-min-w-[368px]">
        <form onSubmit={handleSubmit}>
          {values.merchantIds.map((merchantId, index) => (
            <div key={index} className="cx-flex cx-mx-0 sm:cx-mx-4">
              <div className="cx-items-center cx-mt-4 cx-w-full cx-min-w-0">
                <InputField
                  label={`Merchant ID ${index + 1}`}
                  inputProps={{
                    value: merchantId,
                    onChange: (e) => updateMerchantId(e.target.value, index),
                    onBlur: () => {
                      if (
                        Array.isArray(touched.merchantIds) &&
                        !touched.merchantIds?.[index]
                      ) {
                        setFieldTouched(`merchantIds[${index}]`, true);
                      }
                    },
                  }}
                  tick={
                    Array.isArray(touched.merchantIds) &&
                    Array.isArray(errors.merchantIds) &&
                    touched.merchantIds?.[index] &&
                    !errors.merchantIds?.[index]
                  }
                  error={
                    Array.isArray(touched.merchantIds) &&
                    Array.isArray(errors.merchantIds) &&
                    touched.merchantIds?.[index] &&
                    errors.merchantIds?.[index]
                      ? errors.merchantIds[index]
                      : ""
                  }
                />
              </div>
              {values.merchantIds.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeMerchantId(index)}
                  className={`cx-ml-2 cx-text-red-500 cx-mt-8 cx-flex-shrink-0 cx-relative cx-flex cx-items-center cx-justify-center cx-rounded-full cx-border cx-border-stroke-primary cx-bg-white cx-w-7 cx-h-7 cx-shadow-sm cx-leading-none ${
                    values.merchantIds.length === 1
                      ? "cx-opacity-50 cx-cursor-not-allowed"
                      : "cx-cursor-pointer hover:cx-shadow-base"
                  }`}
                  style={{ padding: 0, lineHeight: 0 }}
                  disabled={values.merchantIds.length === 1}
                  aria-label="Remove Merchant ID"
                >
                  <HyphenIcon className="cx-w-4 cx-h-4 cx-pointer-events-none cx-justify-items-center " />
                </button>
              )}
            </div>
          ))}
          {values.merchantIds.length < 5 && (
            <div className="cx-flex cx-flex-wrap cx-my-2 cx-items-center cx-gap-x-1 cx-gap-y-1 cx-mx-0 sm:cx-mx-4">
              <PlusIcon
                className="cx-stroke-brand-primary-regular cx-shrink-0"
                height={"16"}
                width={"16"}
              />
              <button
                type="button"
                onClick={addMerchantId}
                className="cx-text-brand-primary-regular cx-cursor-pointer cx-bg-transparent cx-border-0 cx-p-0 cx-text-base cx-font-normal"
              >
                Add another Merchant ID
              </button>
              <div className="cx-text-text-secondary cx-font-medium cx-text-sm">
                (you can add up to 5)
              </div>
            </div>
          )}
          <div className="cx-text-text-secondary cx-font-light cx-text-sm cx-flex cx-flex-wrap cx-items-center cx-justify-center cx-gap-x-1 cx-gap-y-1 cx-text-center max-md:cx-mt-5">
            <InfoIcon
              height={"16"}
              width={"16"}
              className="cx-stroke-brand-primary-regular cx-mr-2 cx-shrink-0"
            />
            <span>Learn how to find Merchant ID.</span>
            <button
              type="button"
              className="cx-text-brand-primary-regular cx-cursor-pointer cx-bg-transparent cx-border-0 cx-p-0 cx-text-sm cx-font-light"
              onClick={() => {
                setOpenInfo(true);
              }}
            >
              Click here
            </button>
          </div>
          <div className="cx-my-10">
            <Divider />
          </div>

          {duplicatedMerchant && (
            <div className="cx-text-xs  cx-flex cx-text-text-error cx-items-center cx-justify-center">
              Duplicated Merchant Id
            </div>
          )}
          <div className="cx-w-full cx-flex cx-flex-row cx-gap-x-4">
            <StepNextButton
              label="Next"
              type="submit"
              loading={isLoading}
              disabled={duplicatedMerchant || isLoading || isSubmitting}
            />
          </div>
        </form>
      </div>

      {openInfo && (
        <Modal
          isOpen={openInfo}
          onClose={() => {
            setOpenInfo(!openInfo);
          }}
          title="How to find Merchant ID"
          subtitle="Here are some ways from where you can find MID:"
        >
          <div className="cx-flex cx-flex-col cx-items-start cx-gap-6 cx-w-full cx-min-w-0">
            {infoItems.map((item) => (
              <Accordion
                key={item.title}
                heading={
                  <div className="cx-flex cx-items-center cx-min-w-0 cx-w-full">
                    <span className="cx-shrink-0 cx-flex cx-items-center">
                      {item.icon}
                    </span>
                    <span className="cx-text-brand-primary-regular cx-min-w-0 cx-break-words">
                      {item.title}
                    </span>
                  </div>
                }
              >
                {item.content}
              </Accordion>
            ))}
          </div>
          <div className="cx-w-full cx-mt-10">
            <Button
              type={"button"}
              fullWidth
              label={
                <div className="cx-flex">
                  <ArrowLeftIcon />
                  <span className="cx-ml-2">Go Back</span>
                </div>
              }
              onClick={() => {
                setOpenInfo(false);
              }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};
